import _ from 'lodash'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import SqaureLoader from '../../../components/Loader/SqaureLoader'
import { getVisitReportDetails } from '../../../services/Rapports/VisitReportApi'
import { OBJET_LABELS, PHOTO_BASE_URL, SCALE, SECTION_TITLES } from './visitReportConstants'

/**
 * Panneau latéral affichant un rapport complet, dans l'ordre du formulaire mobile.
 * Fermeture : bouton, touche Échap ou clic sur le fond.
 */
export default function VisitReportDetail({ id, t, onClose }) {
    const [loading, setloading] = useState(true)
    const [data, setdata] = useState(null)
    const [photo, setphoto] = useState(null)
    const closeRef = useRef(null)

    useEffect(() => {
        setloading(true)
        getVisitReportDetails(id)
            .then((result) => {
                if (result?.data?.success === 1) setdata(result.data)
            })
            .finally(() => setloading(false))
    }, [id])

    useEffect(() => {
        closeRef.current?.focus()
        const onKey = (e) => {
            if (e.key === 'Escape') photo ? setphoto(null) : onClose()
        }
        document.addEventListener('keydown', onKey)
        document.body.style.overflow = 'hidden'
        return () => {
            document.removeEventListener('keydown', onKey)
            document.body.style.overflow = ''
        }
    }, [onClose, photo])

    const h = data?.header
    const ratingsBySection = _.groupBy(data?.ratings || [], 'section')

    return (
        <div className="vrr-overlay" onClick={onClose}>
            <aside
                className="vrr-drawer"
                role="dialog"
                aria-modal="true"
                aria-label={t('Détail du rapport de visite')}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="vrr-drawer-head">
                    <div>
                        <h2>{h ? (h.nompdv || h.pdv_code) : t('Rapport de visite')}</h2>
                        {h && (
                            <p>
                                {moment(h.visit_date).format('dddd D MMMM YYYY [à] HH:mm')}<br />
                                {h.user || h.user_code}
                                {h.accompagnement === 1 && <span className="vrr-tag">{t('Visite accompagnée')}</span>}
                            </p>
                        )}
                    </div>
                    <button ref={closeRef} type="button" className="vrr-close" onClick={onClose} aria-label={t('Fermer')}>
                        <i className="fe fe-x" />
                    </button>
                </div>

                <div className="vrr-drawer-body">
                    {loading ? <SqaureLoader /> : !data ? (
                        <p className="vrr-muted">{t('Ce rapport est introuvable. Il a peut-être été supprimé.')}</p>
                    ) : (
                        <>
                            {/* 2. Objet de la visite */}
                            <DetailBlock title={t('Objet de la visite')}>
                                <div className="vrr-chips">
                                    {data.objets.map((o) => (
                                        <span key={o.id} className="vrr-chip">
                                            {o.objet_code === 'AUTRE'
                                                ? `${t('Autre')} : ${o.precision_autre || '–'}`
                                                : t(OBJET_LABELS[o.objet_code] || o.libelle)}
                                        </span>
                                    ))}
                                </div>
                            </DetailBlock>

                            {/* 3. Personnes rencontrées */}
                            <DetailBlock title={t('Personnes rencontrées')}>
                                {data.personnes.length === 0 ? <Empty t={t} /> : (
                                    <div className="vrr-chips">
                                        {data.personnes.map((p) => <span key={p.id} className="vrr-chip vrr-chip-soft">{t(p.personne)}</span>)}
                                    </div>
                                )}
                            </DetailBlock>

                            {/* 4. Points discutés */}
                            <DetailBlock title={t('Points discutés')}>
                                {data.topics.length === 0 ? <Empty t={t} /> : (
                                    <ul className="vrr-items">
                                        {data.topics.map((tp) => (
                                            <li key={tp.id}>
                                                <b>{t(tp.sujet)}</b>
                                                {tp.action ? <p>{tp.action}</p> : <p className="vrr-muted">{t('Aucune action notée')}</p>}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </DetailBlock>

                            {/* 5. Évaluation merchandising */}
                            <DetailBlock title={t('Évaluation merchandising')}>
                                {data.ratings.length === 0 ? <Empty t={t} /> : Object.keys(SECTION_TITLES).map((section) =>
                                    ratingsBySection[section] ? (
                                        <div key={section} className="vrr-eval-group">
                                            <h4>{section} {t(SECTION_TITLES[section])}</h4>
                                            {ratingsBySection[section].map((r) => {
                                                const s = SCALE.find((x) => x.note === Number(r.note))
                                                return (
                                                    <div key={r.id} className="vrr-eval-row">
                                                        <span>{t(r.critere)}</span>
                                                        <span className="vrr-eval-note" style={{ '--note-color': s?.color }}>
                                                            <b>{r.note}</b> {t(s?.label)}
                                                        </span>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    ) : null
                                )}
                            </DetailBlock>

                            {/* 6. Opportunités */}
                            <DetailBlock title={t('Opportunités détectées')}>
                                {data.opportunities.length === 0 ? <Empty t={t} /> : (
                                    <ul className="vrr-items">
                                        {data.opportunities.map((op) => (
                                            <li key={op.id}>
                                                <b>{t(op.opportunite)}</b>
                                                {op.priorite === 1 && <span className="vrr-tag vrr-tag-hot">{t('Prioritaire')}</span>}
                                                {op.priorite === 0 && <span className="vrr-tag">{t('Non prioritaire')}</span>}
                                                {op.action && <p>{op.action}</p>}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </DetailBlock>

                            {/* 7. Veille concurrentielle */}
                            <DetailBlock title={t('Veille concurrentielle')}>
                                {data.veille.length === 0 ? <Empty t={t} /> : (
                                    <ul className="vrr-items">
                                        {data.veille.map((v) => (
                                            <li key={v.id} className={v.photo_path ? 'vrr-veille' : ''}>
                                                <div>
                                                    <b>{t(v.marque)}</b>
                                                    {v.observation ? <p>{v.observation}</p> : null}
                                                </div>
                                                {v.photo_path && (
                                                    <button type="button" className="vrr-thumb"
                                                        onClick={() => setphoto(PHOTO_BASE_URL + v.photo_path)}
                                                        aria-label={`${t('Agrandir la photo')} ${t(v.marque)}`}>
                                                        <img src={PHOTO_BASE_URL + v.photo_path} alt="" loading="lazy" />
                                                    </button>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </DetailBlock>

                            {/* 9. Remarques */}
                            <DetailBlock title={t('Autres remarques')}>
                                {h.remarques ? <p className="vrr-remarques">{h.remarques}</p> : <Empty t={t} />}
                            </DetailBlock>
                        </>
                    )}
                </div>
            </aside>

            {photo && (
                <div className="vrr-lightbox" onClick={(e) => { e.stopPropagation(); setphoto(null) }}>
                    <img src={photo} alt={t('Photo de veille concurrentielle')} />
                    <a href={photo} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                        {t('Ouvrir l\'original')}
                    </a>
                </div>
            )}
        </div>
    )
}

function DetailBlock({ title, children }) {
    return (
        <section className="vrr-block">
            <h3>{title}</h3>
            {children}
        </section>
    )
}

function Empty({ t }) {
    return <p className="vrr-muted">{t('Non renseigné')}</p>
}
